import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

/**
 * Returns a scaled icon based on the current zoom level.
 * @param {object} map - The Leaflet map instance.
 * @param {string} iconUrl - URL of the icon image.
 * @param {Array} baseSize - Base [width, height] of the icon.
 * @param {Array} baseAnchor - Base [x, y] anchor point.
 * @param {number} offsetX - Extra x-offset (to move right).
 * @param {number} offsetY - Extra y-offset (to move down or up; use negative to move up).
 * @param {number} baseline - A baseline zoom level for scaling (default 20).
 */
function getScaledIcon(map, iconUrl, baseSize = [25, 41], baseAnchor = [12, 41], offsetX = 18, offsetY = 0, baseline = 20) {
  const zoom = map.getZoom();
  const scale = zoom / baseline;
  return L.icon({
    iconUrl: iconUrl,
    iconSize: [baseSize[0] * scale, baseSize[1] * scale],
    // Add the offsets then scale. Use negative offsetY to move upward.
    iconAnchor: [(baseAnchor[0] + offsetX) * scale, (baseAnchor[1] + offsetY) * scale],
  });
}

function SpecialMarkers({ markersFileUrl }) {
  const map = useMap();
  // Use a ref to store marker info so we can update them on zoom changes.
  const markersRef = useRef([]);

  useEffect(() => {
    fetch(markersFileUrl)
      .then(response => response.json())
      .then(data => {
        data.forEach(markerData => {
          const { position, name, description, icon, size, anchor, offset } = markerData;
          // Use provided values or fall back to defaults.
          const baseSize = size || [25, 41];
          const baseAnchor = anchor || [12, 41];
          let offsetX = 18, offsetY = 0;
          if (offset && Array.isArray(offset)) {
            offsetX = offset[0];
            offsetY = offset[1];
          }
          
          // Create the scaled icon based on current zoom.
          const scaledIcon = getScaledIcon(map, icon, baseSize, baseAnchor, offsetX, offsetY);
          // Create the marker and add it to the map.
          const marker = L.marker(position, { icon: scaledIcon }).addTo(map);
          // Bind a popup with the marker's name and description.
          marker.bindPopup(`<strong>${name}</strong><br>${description}`);
          marker.on('mouseover', () => marker.openPopup());
          marker.on('mouseout', () => marker.closePopup());
          
          // Save the marker and its base properties for later updates.
          markersRef.current.push({ marker, icon, baseSize, baseAnchor, offsetX, offsetY });
        });
      })
      .catch(error => {
        console.error('Error loading special markers:', error);
      });
  }, [markersFileUrl, map]);

  // Update the marker icons when the zoom level changes.
  useEffect(() => {
    const updateMarkers = () => {
      markersRef.current.forEach(item => {
        const { marker, icon, baseSize, baseAnchor, offsetX, offsetY } = item;
        marker.setIcon(getScaledIcon(map, icon, baseSize, baseAnchor, offsetX, offsetY));
      });
    };
    map.on('zoomend', updateMarkers);
    return () => {
      map.off('zoomend', updateMarkers);
    };
  }, [map]);

  return null;
}

export default SpecialMarkers;
